import axios from 'axios';
import dayjs from 'dayjs';
import api from '../plugins/api.js';

const store = {
    namespaced: true,
    state: {
        user: {
            full_name: null,
            email: null,
            id: null,
            hasAccessToLms: null,
            activeSubscriptionPaymentInterval: null,
            orderUpdate: null,
        },
        isUserDataLoaded: false,
        userCardInfo: {},
        invoices: [],
        numberOfInvoices: null,
        currentSubscription: {},
        activeSubscriptionSalvageOfferUsed: false,
        salvageOfferDiscountedPrice: '',
        salvageOfferExpirationDate: '',
        savedSchools: [],
        savedScholarships: [],
        onboardingWizardStatus: '',
    },
    actions: {
        getUserData({ commit }) {
            return axios
                .get('/is-logged')
                .then((res) => {
                    if (res.data.status === 'success') {
                        commit('saveUserData', res.data.user);
                    } else {
                        commit('saveAnonUserData');
                    }
                })
                .catch((err) => {
                    commit('saveAnonUserData');
                });
        },
        signIn({ commit }, userData) {
            return axios.post('/login', userData).then((res) => {
                commit('saveUserData', res.data);
            });
        },
        resetPassword({}, emailData) {
            return axios.post('/password/email', emailData);
        },
        getUserCardDetails({ commit }) {
            return api.get('stripeCards/default').then((res) => {
                if (res.data.data.length === 0) {
                    return;
                }

                const attributes = res.data.data.attributes;
                const cardInfo = {
                    cardBrand: attributes.brand,
                    fullname: attributes.name,
                    cardNumberCaption: `****-****-**** ${attributes.lastFourDigits}`,
                    expiryDateCaption: `Expires on ${attributes.expiryMonth}/${attributes.expiryYear}`,
                };

                commit('saveUserCardInfo', cardInfo);
            });
        },
        addNewUserCard({ commit }, tokenObject) {
            return api.post('stripeCards', tokenObject);
        },
        getUserSubscriptions({ state, commit }, pageNumber) {
            const queryParams = {
                sort: '-id',
                'filter[paid]': '1',
                'page[number]': pageNumber,
                'page[size]': 3, // 3 invoices per page
            };

            return api.get(`users/${state.user.id}/orders?include=product`, { params: queryParams }).then((res) => {
                const invoices = transformInvoices(res);

                commit('setNumberOfInvoices', res.data.meta.total);
                commit('saveInvoices', invoices);
            });
        },
        sendEmailInvoice({}, wcOrderId) {
            return api.post(`orders/${wcOrderId}/sendInvoice`);
        },
        getCurrentUserSubscription({ state, commit }) {
            const queryParams = {
                'filter[active]': '1',
            };

            return api.get(`users/${state.user.id}/subscriptions`, { params: queryParams }).then((res) => {
                if (res.data.data.length === 0) {
                    return;
                }

                const currentSubscription = transformSubscription(res);

                commit('saveCurrentSubscription', currentSubscription);
            });
        },
        getLastUserSubscription({ state, commit }) {
            const queryParams = {
                sort: '-id',
                'page[size]': 1,
            };

            return api.get(`users/${state.user.id}/subscriptions`, { params: queryParams }).then((res) => {
                if (res.data.data.length === 0) {
                    return;
                }

                const subscription = transformSubscription(res);

                commit('saveCurrentSubscription', subscription);
            });
        },
        cancelSubscription({ state }) {
            return api.patch(`userSubscriptions/${state.currentSubscription.id}/cancel`).then(() => {
                state.currentSubscription.status = 'pending-cancel';
            });
        },
        sendSalvageOfferEmail({ state }) {
            return api.post('salvageOffers/sendEmail').then((res) => {
                const attributes = res.data.data.attributes;

                const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                });

                state.salvageOfferDiscountedPrice = formatter.format(parseFloat(attributes.discountedPrice));
                state.salvageOfferExpirationDate = dayjs(attributes.expirationDate).format('MMMM D, YYYY');
                state.activeSubscriptionSalvageOfferUsed = true;
            });
        },
        getUserSavedSchools({ commit }) {
            return api.get('userSavedSchools').then((res) => {
                const savedSchools = res.data.data.map(({ id, attributes }) => {
                    return {
                        id: id,
                        title: attributes.name,
                        url: attributes.url,
                        additionalText: `${attributes.city}, ${attributes.state}`,
                        logo: attributes.logoUrl,
                    };
                });

                commit('saveSavedSchools', savedSchools);
            });
        },
        deleteSavedSchool({ dispatch, commit }, id) {
            return axios.post(`/remove/school/${id}`).then(() => {
                dispatch('getUserSavedSchools');
            });
        },
        getUserSavedScholarships({ commit }) {
            return api.get('userSavedScholarships').then((res) => {
                const savedScholarships = res.data.data.map(({ id, attributes }) => {
                    return {
                        id: id,
                        title: attributes.name,
                        url: attributes.url,
                        additionalText: attributes.sponsor,
                    };
                });

                commit('saveSavedScholarships', savedScholarships);
            });
        },
        deleteSavedScholarship({ dispatch, commit }, id) {
            return axios.post(`/remove/scholarship/${id}`).then(() => {
                dispatch('getUserSavedScholarships');
            });
        },
        updateOnboardingWizardStatus({ commit }, status) {
            commit('saveOnboardingWizardStatus', status);
        },
    },
    mutations: {
        saveUserData(state, user) {
            state.isUserDataLoaded = true;
            state.user.full_name = user.full_name;
            state.user.email = user.email;
            state.user.id = user.id;
            state.user.hasAccessToLms = user.hasAccessToLms;
            state.user.activeSubscriptionPaymentInterval = user.activeSubscriptionPaymentInterval;
            state.user.activeSubscriptionWooCommerceOrderId = user.activeSubscriptionWooCommerceOrderId;
            state.user.activeSubscriptionWooCommerceProductId = user.activeSubscriptionWooCommerceProductId;
            state.user.activeCareerPeach = user.activeCareerPeach;
            state.user.activeVisualCV = user.activeVisualCV;
            state.activeSubscriptionSalvageOfferUsed = user.activeSubscriptionSalvageOfferUsed;
            state.onboardingWizardStatus = user.onboarding_wizard_status;
            state.user.orderUpdate = user.orderUpdate;
        },
        saveAnonUserData(state) {
            state.isUserDataLoaded = true;
        },
        saveUserCardInfo(state, payload) {
            state.userCardInfo = payload;
        },
        saveInvoices(state, payload) {
            state.invoices = payload;
        },
        saveCurrentSubscription(state, payload) {
            state.currentSubscription = payload;
        },
        setNumberOfInvoices(state, payload) {
            state.numberOfInvoices = payload;
        },
        saveSavedSchools(state, payload) {
            state.savedSchools = payload;
        },
        saveSavedScholarships(state, payload) {
            state.savedScholarships = payload;
        },
        saveOnboardingWizardStatus(state, status) {
            state.onboardingWizardStatus = status;
        },
    },
    getters: {
        getUserActiveSubscriptionPaymentInterval(state) {
            return state.user.activeSubscriptionPaymentInterval;
        },
    },
};

function transformSubscription(res) {
    const attributes = res.data.data[0].attributes;

    const timeDifference = new Date(attributes.expirationDate).getTime() - new Date(attributes.purchaseDate).getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);

    const type = attributes.price === attributes.discount ? '100%coupon' : daysDifference <= 7 ? 'freeTrial' : 'regular';

    const subscription = {
        id: res.data.data[0].id,
        type: type,
        createdVia: attributes.createdVia,
        title: getCurrentSubscriptionTitle(type),
        subtitle: getCurrentSubscriptionSubtitle(type, attributes.paymentInterval),
        payInterval: getCurrentSubscriptionPayInterval(type, attributes.price, attributes.paymentInterval),
        price: getCurrentSubscriptionPrice(type, attributes.price, attributes.paymentInterval),
        pricePer: type === 'regular' ? '/mo' : '',
        interval: attributes.paymentInterval,
        purchased: dayjs(attributes.purchaseDate).format('MMMM D, YYYY'),
        nextBillingDate: type === '100%coupon' ? '-' : dayjs(attributes.expirationDate).format('MMMM D, YYYY'),
        status: attributes.status,
        wcOrderId: attributes.wcOrderId,
    };

    return subscription;
}

function transformInvoices(res) {
    const data = res.data.data;
    const included = res.data.included;

    return data.map((item) => {
        const product = included.find((el) => item.relationships.product.data.id === el.id);

        return {
            id: item.id,
            wcOrderId: item.attributes.wcOrderId,
            title: product.attributes.description,
            date: dayjs(item.attributes.purchaseDate).format('MMMM D, YYYY'),
            price: `$${item.attributes.price}`,
            total: `$${item.attributes.total}`,
        };
    });
}

function getCurrentSubscriptionTitle(type) {
    const titles = {
        freeTrial: 'Peterson’s Test Prep Free Trial',
        '100%coupon': 'Peterson’s Test Prep',
        regular: 'Peterson’s Test Prep Subscription',
    };

    return titles[type];
}

function getCurrentSubscriptionSubtitle(type, paymentInterval) {
    const payIntervals = {
        freeTrial: '7-day free trial',
        '100%coupon': 'Free plan',
        regular: `${paymentInterval}-month Subscription Plan`,
    };

    return payIntervals[type];
}

function getCurrentSubscriptionPayInterval(type, price, paymentInterval) {
    const payIntervals = {
        freeTrial: `Charged $${price} after 7 days`,
        '100%coupon': '',
        regular: `Pay every ${paymentInterval === 1 ? 'month' : paymentInterval + ' months'}`,
    };

    return payIntervals[type];
}

function getCurrentSubscriptionPrice(type, price, paymentInterval) {
    const prices = {
        freeTrial: '$0',
        '100%coupon': '$0',
        regular: `$${Math.ceil(Number(price) / Number(paymentInterval))}`,
    };

    return prices[type];
}

export default store;
